import { ref, computed } from 'vue';
import { billingService, type Subscription, type PlanFeatures } from '../services/billing';

// Module-level state (shared across all components using this composable)
const subscription = ref<Subscription | null>(null);
const features = ref<Partial<PlanFeatures> | null>(null);
const entitledSolutions = ref<string[]>([]);
const entitledSolutionsLoaded = ref(false);
const userType = ref<string | null>(null);
const loading = ref(true);
const loaded = ref(false);

/**
 * Phase 2.10/2.13 — read the `entitled_solutions` claim out of the access
 * token. Returns `{ loaded: false }` if the token is missing, malformed,
 * or has no claim (e.g. older token issued before Phase 2.10).
 */
function readSolutionClaimFromToken(): {
  loaded: boolean;
  list: string[];
  userType: string | null;
} {
  const token =
    localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
  if (!token) return { loaded: false, list: [], userType: null };
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return { loaded: false, list: [], userType: null };
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))
    ) as { entitled_solutions?: unknown; user_type?: unknown };
    const ut =
      typeof payload.user_type === 'string' ? payload.user_type : null;
    if ('entitled_solutions' in payload) {
      const raw = payload.entitled_solutions;
      const list = Array.isArray(raw)
        ? raw.filter((s): s is string => typeof s === 'string')
        : [];
      return { loaded: true, list, userType: ut };
    }
    return { loaded: false, list: [], userType: ut };
  } catch {
    return { loaded: false, list: [], userType: null };
  }
}

export function useEntitlements() {
  const planType = computed(() => subscription.value?.plan_type ?? 'free');

  /**
   * Load subscription, features, and solution licenses. Called once on app
   * mount. Fail-open: if billing service is unreachable, features stays
   * null and all hasFeature() checks return true. Solution licenses come
   * from the JWT claim, so they're always available without an API call.
   */
  async function load(): Promise<void> {
    // Always re-read the solution claim — it's cheap (no I/O) and reflects
    // the most recent token, which may have been refreshed.
    const claim = readSolutionClaimFromToken();
    entitledSolutions.value = claim.list;
    entitledSolutionsLoaded.value = claim.loaded;
    userType.value = claim.userType;

    if (loaded.value) return; // Subscription side already loaded this session
    loading.value = true;
    try {
      const sub = await billingService.getCurrentSubscription();
      subscription.value = sub;
      features.value = sub?.features ?? null;
    } catch {
      // Fail open — billing outage should never lock users out of the UI
      subscription.value = null;
      features.value = null;
    } finally {
      loading.value = false;
      loaded.value = true;
    }
  }

  /**
   * Check if a specific feature is enabled.
   * @param featurePath - dot-notation path, e.g. "channels.voice" or "ai.rag_knowledge_base"
   * @returns true if feature is enabled, or true if features not loaded (fail open)
   */
  function hasFeature(featurePath: string): boolean {
    if (!features.value) return true; // fail open

    const [category, feature] = featurePath.split('.');
    if (!category || !feature) return true;

    const categoryFeatures = (features.value as Record<string, Record<string, boolean>>)[category];
    if (!categoryFeatures) return true; // unknown category — fail open

    return categoryFeatures[feature] ?? false;
  }

  /**
   * Check if ANY of the given features are enabled.
   * Used for group-level gating where a group requires at least one feature.
   */
  function hasAnyFeature(featurePaths: string[]): boolean {
    if (!features.value) return true; // fail open
    return featurePaths.some(f => hasFeature(f));
  }

  /**
   * Check if ANY feature in a category is enabled.
   * Used for wildcard checks like "integrations.*" or "channels.*".
   * @param category - category name, e.g. "integrations" or "channels"
   * @param exclude - optional feature keys to exclude from the check
   */
  function hasAnyInCategory(category: string, exclude: string[] = []): boolean {
    if (!features.value) return true; // fail open

    const categoryFeatures = (features.value as Record<string, Record<string, boolean>>)[category];
    if (!categoryFeatures) return true;

    return Object.entries(categoryFeatures).some(
      ([key, value]) => !exclude.includes(key) && value === true
    );
  }

  /**
   * Phase 2.13 — check if the tenant has a license for a specific solution.
   *
   * @param code - solution code from the `SolutionCode` union
   *   (currently "core" | "va" | "assist" | "fax" | "dars"; see
   *   solutionLicenses.ts for the source of truth).
   * @returns
   *   - super-admin → always true (operates cross-tenant)
   *   - claim not loaded (older token) → true (fail open during transition)
   *   - claim loaded → strict membership check
   */
  function isSolutionLicensed(code: string): boolean {
    if (userType.value === 'super_admin') return true;
    if (!entitledSolutionsLoaded.value) return true; // fail open
    return entitledSolutions.value.includes(code);
  }

  /**
   * Check if the tenant has at least one of the given solution licenses.
   * Same fail-open semantics as `isSolutionLicensed`.
   */
  function hasAnySolution(codes: string[]): boolean {
    if (userType.value === 'super_admin') return true;
    if (!entitledSolutionsLoaded.value) return true; // fail open
    return codes.some(c => entitledSolutions.value.includes(c));
  }

  /**
   * Reset state (useful for logout)
   */
  function reset(): void {
    subscription.value = null;
    features.value = null;
    entitledSolutions.value = [];
    entitledSolutionsLoaded.value = false;
    userType.value = null;
    loading.value = true;
    loaded.value = false;
  }

  return {
    subscription,
    features,
    entitledSolutions,
    entitledSolutionsLoaded,
    userType,
    loading,
    loaded,
    planType,
    load,
    hasFeature,
    hasAnyFeature,
    hasAnyInCategory,
    isSolutionLicensed,
    hasAnySolution,
    reset,
  };
}
